import { Platform } from 'react-native';
import {
    DASHBOARD_ACTIVATION_ROUTE_PATTERN,
    DASHBOARD_DELIVERY_ROUTE_PATTERN,
    DASHBOARD_NEW_ROUTE_PATTERN,
} from './dashboardRoutes';
import {
    isHomeRedirectPath,
    normalizePostAuthRedirect,
} from './postAuthRedirect';
import { buildProfileCompletionRoute } from './profileCompletion';

export {
    isHomeRedirectPath,
    normalizePostAuthRedirect,
} from './postAuthRedirect';

export type FunnelSource = 'chat' | 'form' | 'template' | 'signup' | 'home' | 'forward' | 'direct';
export type FunnelStep = 'chat' | 'form' | 'processing' | 'delivery' | 'activation' | 'profile';
export type FunnelProcessingState = 'active' | 'stalled' | 'completed';

export interface FunnelContext {
    source: FunnelSource;
    step: FunnelStep;
    projectId?: string;
    intentId?: string;
    activationGeneration?: number;
    finalizationRequestId?: string;
    journeyId?: string;
    redirectTo: string;
    processingState?: FunnelProcessingState;
}

export interface PartialFunnelContext {
    source?: FunnelSource;
    step?: FunnelStep;
    projectId?: string;
    intentId?: string;
    activationGeneration?: number;
    finalizationRequestId?: string;
    journeyId?: string;
    redirectTo?: string;
    processingState?: FunnelProcessingState;
}

export const FUNNEL_QUERY_KEYS = {
    redirectTo: 'redirect_to',
    source: 'funnel_source',
    step: 'funnel_step',
    projectId: 'funnel_project_id',
    intentId: 'funnel_intent_id',
    activationGeneration: 'activation_generation',
    finalizationRequestId: 'finalization_request_id',
    journeyId: 'funnel_journey_id',
    processingState: 'processing_state',
} as const;

const FUNNEL_PATH_PATTERNS: RegExp[] = [
    DASHBOARD_NEW_ROUTE_PATTERN,
    DASHBOARD_DELIVERY_ROUTE_PATTERN,
    DASHBOARD_ACTIVATION_ROUTE_PATTERN,
    /^\/complete-profile(?:\/|$)/,
];

function isValidSource(value: string | undefined): value is FunnelSource {
    return value === 'chat'
        || value === 'form'
        || value === 'template'
        || value === 'signup'
        || value === 'home'
        || value === 'forward'
        || value === 'direct';
}

function isValidStep(value: string | undefined): value is FunnelStep {
    return value === 'chat'
        || value === 'form'
        || value === 'processing'
        || value === 'delivery'
        || value === 'activation'
        || value === 'profile';
}

function parseActivationGeneration(value: string | undefined): number | undefined {
    if (!value || !/^\d+$/.test(value)) {
        return undefined;
    }
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function parseRequestId(value: string | undefined): string | undefined {
    return value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
        ? value
        : undefined;
}

function isValidProcessingState(value: string | undefined): value is FunnelProcessingState {
    return value === 'active' || value === 'stalled' || value === 'completed';
}

export function isFunnelPath(pathname: string): boolean {
    return FUNNEL_PATH_PATTERNS.some((pattern) => pattern.test(pathname));
}

export function toSingleParam(
    value: string | string[] | undefined
): string | undefined {
    if (Array.isArray(value)) {
        return value[0];
    }
    return value;
}

export function parseFunnelContextFromParams(
    params: Record<string, string | string[] | undefined>
): PartialFunnelContext {
    const source = toSingleParam(params[FUNNEL_QUERY_KEYS.source]);
    const step = toSingleParam(params[FUNNEL_QUERY_KEYS.step]);
    const projectId = toSingleParam(params[FUNNEL_QUERY_KEYS.projectId]);
    const intentId = toSingleParam(params[FUNNEL_QUERY_KEYS.intentId]);
    const activationGeneration = toSingleParam(params[FUNNEL_QUERY_KEYS.activationGeneration]);
    const finalizationRequestId = toSingleParam(params[FUNNEL_QUERY_KEYS.finalizationRequestId]);
    const journeyId = toSingleParam(params[FUNNEL_QUERY_KEYS.journeyId]);
    const redirectTo = toSingleParam(params[FUNNEL_QUERY_KEYS.redirectTo]);
    const processingState = toSingleParam(params[FUNNEL_QUERY_KEYS.processingState]);

    return {
        source: isValidSource(source) ? source : undefined,
        step: isValidStep(step) ? step : undefined,
        projectId,
        intentId,
        activationGeneration: parseActivationGeneration(activationGeneration),
        finalizationRequestId: parseRequestId(finalizationRequestId),
        journeyId,
        redirectTo,
        processingState: isValidProcessingState(processingState) ? processingState : undefined,
    };
}

export function getCurrentRedirectTo(pathname: string): string {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
        return `${window.location.pathname}${window.location.search}`;
    }

    return pathname || '/';
}

export function appendQueryParams(url: string, values: Record<string, string | undefined>): string {
    const [base, existingQuery = ''] = url.split('?');
    const searchParams = new URLSearchParams(existingQuery);

    Object.entries(values).forEach(([key, value]) => {
        if (value) {
            searchParams.set(key, value);
        }
    });

    const query = searchParams.toString();
    return query ? `${base}?${query}` : base;
}

export function withFunnelAuthParams(
    authCallbackUrl: string,
    redirectTo?: string,
    funnelContext?: PartialFunnelContext
): string {
    return appendQueryParams(authCallbackUrl, {
        [FUNNEL_QUERY_KEYS.redirectTo]: redirectTo,
        [FUNNEL_QUERY_KEYS.source]: funnelContext?.source,
        [FUNNEL_QUERY_KEYS.step]: funnelContext?.step,
        [FUNNEL_QUERY_KEYS.projectId]: funnelContext?.projectId,
        [FUNNEL_QUERY_KEYS.intentId]: funnelContext?.intentId,
        [FUNNEL_QUERY_KEYS.activationGeneration]: funnelContext?.activationGeneration?.toString(),
        [FUNNEL_QUERY_KEYS.finalizationRequestId]: funnelContext?.finalizationRequestId,
        [FUNNEL_QUERY_KEYS.journeyId]: funnelContext?.journeyId,
        [FUNNEL_QUERY_KEYS.processingState]: funnelContext?.processingState,
    });
}

export function buildCompleteProfileRoute(
    redirectTo?: string,
    funnelContext?: PartialFunnelContext
): string {
    return buildProfileCompletionRoute(redirectTo, funnelContext);
}

export function inferFunnelContextFromRoute(
    pathname: string,
    params: Record<string, string | string[] | undefined>
): FunnelContext | null {
    if (!isFunnelPath(pathname) && pathname !== '/') {
        return null;
    }

    const from = toSingleParam(params.from);
    const processingState = toSingleParam(params[FUNNEL_QUERY_KEYS.processingState]);

    const redirectTo = getCurrentRedirectTo(pathname);

    if (pathname === '/') {
        if (toSingleParam(params.openChat) === 'true' || toSingleParam(params.funnel) === 'true') {
            return {
                source: 'home',
                step: 'chat',
                journeyId: toSingleParam(params[FUNNEL_QUERY_KEYS.journeyId]),
                redirectTo,
            };
        }
        return null;
    }

    if (DASHBOARD_NEW_ROUTE_PATTERN.test(pathname)) {
        const projectId = toSingleParam(params.projectId);

        return {
            source: 'form',
            step: 'form',
            projectId,
            journeyId: toSingleParam(params[FUNNEL_QUERY_KEYS.journeyId]),
            redirectTo,
            processingState: isValidProcessingState(processingState) ? processingState : undefined,
        };
    }

    const deliveryMatch = pathname.match(/^\/(?:digests|dashboard)\/([^/]+)\/delivery(?:\/|$)/);
    if (deliveryMatch) {
        const source: FunnelSource = from === 'form'
            ? 'form'
            : 'chat';

        return {
            source,
            step: 'delivery',
            projectId: deliveryMatch[1],
            journeyId: toSingleParam(params[FUNNEL_QUERY_KEYS.journeyId]),
            redirectTo,
            processingState: isValidProcessingState(processingState) ? processingState : undefined,
        };
    }

    const activationMatch = pathname.match(/^\/(?:digests|dashboard)\/([^/]+)\/activate(?:\/|$)/);
    if (activationMatch) {
        const parsed = parseFunnelContextFromParams(params);
        return {
            source: parsed.source || 'chat',
            step: 'activation',
            projectId: activationMatch[1],
            intentId: parsed.intentId,
            activationGeneration: parsed.activationGeneration,
            finalizationRequestId: parsed.finalizationRequestId,
            journeyId: parsed.journeyId,
            redirectTo,
            processingState: parsed.processingState,
        };
    }

    if (pathname.startsWith('/complete-profile')) {
        const parsed = parseFunnelContextFromParams(params);

        return {
            source: parsed.source || 'signup',
            step: 'profile',
            projectId: parsed.projectId,
            intentId: parsed.intentId,
            activationGeneration: parsed.activationGeneration,
            finalizationRequestId: parsed.finalizationRequestId,
            journeyId: parsed.journeyId,
            redirectTo,
            processingState: parsed.processingState,
        };
    }

    return null;
}
